import { HostInfo } from '@hqo/hqo-miniapp-client-sdk';
import { captureMessage } from '@sentry/react';
import { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';
import { useContextValue } from 'app/shared/hooks/use-context-value';
import { HostPlatform } from 'app/shared/consts';

export interface HostInfoContextValue {
  hostPlatform?: HostPlatform;
  hostInfo?: HostInfo;
}

const HostInfoContext = createContext<HostInfoContextValue>({});

export const HostInfoProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [hostPlatform, setHostPlatform] = useState<HostPlatform>();
  const [hostInfo, setHostInfo] = useState<HostInfo>();
  const client = useMiniappSdk();

  // Fetch host info ONCE for the whole app. Previously useSdkHostInfo held local state, so every
  // consumer (attachable, page-impressions, content-view) fired its own getInfo() — N redundant
  // requests (N×2 under StrictMode), each of which the host dutifully answers. Centralising it
  // here means a single getInfo per session, shared via context.
  useEffect(() => {
    if (client && !hostPlatform) {
      client.host.getInfo();
    }
  }, [client, hostPlatform]);

  useMiniappSdkEventHandler(
    'getHostInfoResponse',
    (data) => {
      // `getHostInfoResponse` should carry an object, but a non-empty string can slip through — e.g. a
      // double-encoded payload the SDK unwraps only once. `data &&` doesn't exclude a string, and
      // `'platform' in <string>` throws `Cannot use 'in' operator…`, so narrow to an object first.
      if (data && typeof data === 'object') {
        if ('platform' in data) {
          setHostPlatform(data.platform as HostPlatform);
        }
        setHostInfo(data as unknown as HostInfo);
      } else if (data != null) {
        // Don't crash — but don't drop it silently either; the next occurrence needs a breadcrumb.
        // (Recovering the double-encoded shape is deferred: the SDK contract is `{ host: HostInfo }`
        // with no top-level `platform`, so populating hostPlatform/hostInfo correctly is a deliberate
        // shape fix, not just a re-parse. See MINIAPP-CONTENT-A.)
        captureMessage('getHostInfoResponse: unexpected non-object payload', {
          level: 'warning',
          extra: { payloadType: typeof data },
        });
      }
    },
    [setHostPlatform, setHostInfo],
  );

  const value = useMemo(() => ({ hostPlatform, hostInfo }), [hostPlatform, hostInfo]);

  return <HostInfoContext.Provider value={value}>{children}</HostInfoContext.Provider>;
};

export const useSdkHostInfo = (): HostInfoContextValue => useContextValue(HostInfoContext);

export { HostInfoContext };
